import { createContext, useContext, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';

// Import the hook you already created in your configuration file
import { useConfiguration } from '@/contexts/ConfigContext';

const DirectionContext = createContext(false);

export function DirectionProvider({ children }) {
  // 1. Grab the config directly from your existing context
  const { config, isConfigPending } = useConfiguration();
  const { locale } = useRouter();
  // 1. Find the object in the 'available' array that matches the current locale
  // Fall back to the "default" key if the current locale isn't found
  const isRTL = useMemo(() => {
    if (!config?.languages || !Array.isArray(config.languages.available)) return false;

    const currentLocale = locale || config.languages.default;
    const activeLangConfig = config.languages.available.find(lang => lang.value === currentLocale);
    return activeLangConfig?.display?.layout?.rightToLeft?.enabled ?? false;
  }, [config, locale]);

  useEffect(() => {
    // Only apply direction once the config has actually loaded
    if (!isConfigPending) {
      document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
      // Sync the lang attribute while we're at it
      document.documentElement.lang = locale || 'en';
    }
  }, [isRTL, isConfigPending, config?.languages, locale]);

  return <DirectionContext.Provider value={isRTL}>{children}</DirectionContext.Provider>;
}

export function useIsRtl() {
  return useContext(DirectionContext);
}
